import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

function clamp(s, n = 80) {
  return (s ?? '').toString().trim().slice(0, n);
}

export default function PlanRequestModal({ open, onClose, me, planKey, onSubmit }) {
  const title =
    planKey === 'PRO'
      ? 'PRO TALEBİ'
      : planKey === 'ENTERPRISE'
      ? 'ENTERPRISE TALEBİ'
      : 'PLAN TALEBİ';

  const defaults = useMemo(
    () => ({
      fullName: clamp(me?.fullName, 80),
      companyName: clamp(me?.companyName, 120),
      phone: clamp(me?.companyPhone || '', 30),
    }),
    [me]
  );

  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errText, setErrText] = useState('');

  useEffect(() => {
    if (!open) return;
    setFullName(defaults.fullName);
    setCompanyName(defaults.companyName);
    setPhone(defaults.phone);
    setNote('');
    setSubmitting(false);
    setErrText('');
  }, [open, defaults]);

  const canSubmit =
    (planKey === 'PRO' || planKey === 'ENTERPRISE') &&
    clamp(fullName).length >= 3 &&
    clamp(companyName).length >= 2 &&
    clamp(phone).length >= 10;
  async function handleSubmit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setErrText('');
    try {
      await onSubmit({
        planKey,
        fullName: clamp(fullName, 80),
        companyName: clamp(companyName, 120),
        phone: clamp(phone, 30),
        note: clamp(note, 250),
      });
      onClose();
    } catch (e) {
      setErrText(e?.message || 'Talep gönderilemedi.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon>assignment</Icon>
        <MDBox>
          <MDTypography fontWeight="bold" sx={{ letterSpacing: '0.08em' }}>
            {title}
          </MDTypography>
          <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
            Bilgileri doldur, ekibimiz seninle iletişime geçsin.
          </MDTypography>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
          <MDInput
            label="Ad Soyad"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <MDInput
            label="İşletme Adı"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          <MDInput
            label="Telefon"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="05xx xxx xx xx"
          />
          <MDInput
            label="Not (opsiyonel)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            rows={3}
          />

          {errText ? (
            <MDTypography variant="caption" color="error">
              {errText}
            </MDTypography>
          ) : !canSubmit ? (
            <MDTypography variant="caption" sx={{ opacity: 0.75 }}>
              Lütfen ad-soyad, işletme adı ve telefon numarasını gir.
            </MDTypography>
          ) : null}
        </MDBox>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <MDButton variant="text" color="secondary" onClick={onClose} disabled={submitting}>
          Vazgeç
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Gönderiliyor…' : 'Talep Et'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
